import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import type { ProductSchedule } from '@nucleus-commerce/api-client/admin';

import { schedulesApi } from '../api/schedules-api';

import { refreshAfterRun } from './invalidate';
import { scheduleKeys } from './schedule-keys';

/** A run in progress finishes on the worker; poll until it has. */
const RUNNING_POLL_MS = 5_000;

export function scheduleQueryOptions(id: string) {
  return queryOptions({
    queryKey: scheduleKeys.detail(id),
    queryFn: () => schedulesApi.get(id),
    refetchInterval: (query) => (query.state.data?.runningRunId ? RUNNING_POLL_MS : false),
  });
}

/**
 * The schedule, polled while a run holds the claim. When that run finishes (`runningRunId` goes
 * from set to null) everything the run changed refetches: runs, run items, the pool, the
 * timeline and the target stores' catalogs, so nothing stays on "Running".
 */
export function useSchedule(id: string) {
  const queryClient = useQueryClient();
  const query = useQuery(scheduleQueryOptions(id));
  const running = query.data?.runningRunId ?? null;
  const previous = useRef(running);
  useEffect(() => {
    const was = previous.current;
    previous.current = running;
    if (was === null || running !== null) return;
    const schedule = queryClient.getQueryData<ProductSchedule>(scheduleKeys.detail(id));
    if (schedule) void refreshAfterRun(queryClient, schedule);
  }, [id, queryClient, running]);
  return query;
}

/** The schedule a conflict names, for its name and a link; skipped while `id` is null. */
export function useScheduleName(id: string | null) {
  return useQuery({
    queryKey: scheduleKeys.detail(id ?? ''),
    queryFn: () => schedulesApi.get(id ?? ''),
    enabled: id !== null,
    select: (schedule) => schedule.name,
    meta: { silentError: true },
  });
}
